import type { UsersColumnKey } from "@/modules/users";
export const USERS_SEARCH_PLACEHOLDER = "Buscar nome, e-mail...";

/** Nome base do arquivo exportado quando o serviço não informa um. */
export const USERS_EXPORT_FILE_NAME = "usuarios";
export const USERS_EXPORT_LIST_NAME = "usuários";

export const USERS_FORBIDDEN_MESSAGE = "Você não tem permissão para consultar os usuários do Hub.";
export const USERS_ERROR_MESSAGE = "Não foi possível carregar os usuários.";

export const USERS_LOADING_LABEL = "Carregando usuários...";
export const USERS_ROWS_PER_PAGE_LABEL = "Usuários por página";
export const USERS_TABLE_LABEL = "Usuários cadastrados";

export const TIME_FILTER_KEY = "time";
export const PERFIL_FILTER_KEY = "perfil";
export const TIME_FILTER_LABEL = "Time";
export const PERFIL_FILTER_LABEL = "Perfil";

export const USER_INFO_TITLE = "Informações do Usuário";

export const EMPTY_CELL = "—";

/** Texto da badge do usuário sem perfil (Figma 4966:62732); só a badge usa "papel", o resto da tela diz Perfil. */
export const NO_ROLE_LABEL = "Sem papel";

export type { UsersColumnKey };

export const USERS_PHOTO_KEY = "foto";
export const USERS_ACTIONS_KEY = "acoes";

/** Colunas na ordem do Figma 4434:111149; Foto e Ações não ordenam. */
export const USERS_COLUMNS: readonly {
  readonly key: UsersColumnKey;
  readonly header: string;
  readonly widthRem: number;
}[] = [
  { key: "foto", header: "Foto", widthRem: 3.75 },
  { key: "nome", header: "Nome", widthRem: 8.0625 },
  { key: "email", header: "E-mail", widthRem: 15.3125 },
  { key: "time", header: "Time", widthRem: 6.25 },
  { key: "papel", header: "Perfil", widthRem: 9.375 },
  { key: "ultimaAtividade", header: "Última Atividade", widthRem: 5.9375 },
  { key: "status", header: "Status", widthRem: 5 },
  { key: "acoes", header: "Ações", widthRem: 6.375 },
];

export const USERS_ALL_COLUMNS: readonly UsersColumnKey[] = USERS_COLUMNS.map(({ key }) => key);

/** Colunas que o Configurações da Tabela não deixa ocultar: quem é o usuário e o que se faz com ele. */
export const USERS_MAIN_COLUMNS: readonly UsersColumnKey[] = ["nome", "email", "acoes"];

export const USERS_COLUMN_LABELS: Record<UsersColumnKey, string> = Object.fromEntries(
  USERS_COLUMNS.map(({ key, header }) => [key, header]),
) as Record<UsersColumnKey, string>;

/** Abaixo da soma das larguras do Figma a tabela rola na horizontal. */
export const USERS_TABLE_MIN_WIDTH_REM = USERS_COLUMNS.reduce(
  (total, { widthRem }) => total + widthRem,
  0,
);

/** Retorno da atribuição/alteração de perfil (RN01/RN02). */
export const ROLE_UPDATE_SUCCESS_MESSAGE = "Perfil do usuário atualizado com sucesso.";
export const ROLE_UPDATE_ERROR_MESSAGE = "Não foi possível atualizar o perfil do usuário.";
export const ROLE_UPDATE_FORBIDDEN_MESSAGE =
  "Você não tem permissão para administrar os usuários do Hub.";
export const SESSION_EXPIRED_MESSAGE = "Sua sessão expirou. Entre novamente para continuar.";

/** O administrador precisa distinguir "não tenho permissão" de "deu erro". */
export const ROLE_UPDATE_ERRORS: Record<number, { summary: string; detail: string }> = {
  401: { summary: "Sessão expirada", detail: SESSION_EXPIRED_MESSAGE },
  403: { summary: "Acesso não autorizado", detail: ROLE_UPDATE_FORBIDDEN_MESSAGE },
};

/** Confirmação apresentada ao administrador quando a alteração é gravada. */
export const ACTIVATE_SUCCESS_MESSAGE = "Usuário ativado com sucesso.";
export const DEACTIVATE_SUCCESS_MESSAGE = "Usuário inativado com sucesso.";

export const TOGGLE_STATUS_ERROR_MESSAGE = "Não foi possível alterar o status do usuário.";
export const TOGGLE_STATUS_FORBIDDEN_MESSAGE =
  "Você não tem permissão para alterar o status dos usuários do Hub.";
export const TOGGLE_STATUS_SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Entre novamente no Hub para alterar o status do usuário.";
export const TOGGLE_STATUS_CONFLICT_MESSAGE =
  "O status deste usuário já foi alterado por outra pessoa. Atualize a listagem antes de tentar novamente.";

/**
 * Situações em que repetir a operação não muda o resultado: o modal se fecha e a listagem segue com o status anterior.
 * A falha de gravação não entra na lista, por isso mantém o modal aberto para nova tentativa.
 */
export const TOGGLE_STATUS_ERRORS: Record<number, { summary: string; detail: string }> = {
  // A sessão expirada é tratada pelo cliente HTTP, que redireciona ao login; este ramo cobre um 401 sem o redirecionamento.
  401: { summary: "Sessão expirada", detail: TOGGLE_STATUS_SESSION_EXPIRED_MESSAGE },
  403: { summary: "Acesso não autorizado", detail: TOGGLE_STATUS_FORBIDDEN_MESSAGE },
  409: { summary: "Status já alterado", detail: TOGGLE_STATUS_CONFLICT_MESSAGE },
};
